import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';
import { Button, Icon, Popover, Text } from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';

export type MenuSelectOption = {
  label: string;
  value: string;
  helpText?: string;
};

export function MenuSelect({
  label,
  value,
  options,
  onChange,
  disabled = false,
  compact = false,
}: {
  label: string;
  value: string;
  options: MenuSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const [active, setActive] = useState(false);
  const [activatorWidth, setActivatorWidth] = useState<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth,
  );
  const id = useId();
  const activatorRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.value === value) || options[0];
  const longestLabel = options.reduce((max, option) => Math.max(max, option.label.length), 0);
  const longestHelp = options.reduce((max, option) => Math.max(max, option.helpText?.length || 0), 0);

  useEffect(() => {
    const element = activatorRef.current;
    if (!element) return;

    const measure = () => {
      setActivatorWidth(Math.ceil(element.getBoundingClientRect().width));
      setViewportWidth(window.innerWidth);
    };

    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    window.addEventListener('resize', measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    if (!active || typeof window === 'undefined') return;

    const closeOnPageScroll = (event: Event) => {
      const target = event.target;
      if (
        target === document ||
        target === window ||
        target === document.documentElement ||
        target === document.body
      ) {
        setActive(false);
      }
    };

    window.addEventListener('scroll', closeOnPageScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', closeOnPageScroll, { capture: true });
  }, [active]);

  const minPopoverWidth = compact ? 240 : 300;
  const maxPopoverWidth = compact ? 420 : 560;
  const estimatedWidth = Math.max(
    minPopoverWidth,
    Math.ceil(longestLabel * 7.6) + 92,
    compact ? 0 : Math.min(280, Math.ceil(longestHelp * 4.2)),
  );
  const safeViewportWidth = viewportWidth > 0 ? Math.max(200, viewportWidth - 24) : maxPopoverWidth;
  const preferredWidth = Math.min(
    Math.max(activatorWidth ?? minPopoverWidth, minPopoverWidth, estimatedWidth),
    maxPopoverWidth,
    safeViewportWidth,
  );
  const popoverStyle = {
    '--admin-menu-select-width': `${preferredWidth}px`,
    width: `${preferredWidth}px`,
    maxWidth: 'calc(100vw - 24px)',
    boxSizing: 'border-box',
  } as CSSProperties;

  const activator = (
    <div ref={activatorRef} className="admin-menu-select__activator">
      <Button
        fullWidth
        textAlign="left"
        disclosure={active ? 'up' : 'down'}
        disabled={disabled}
        ariaControls={id}
        ariaExpanded={active}
        onClick={() => setActive((current) => !current)}
      >
        {selectedOption?.label || '—'}
      </Button>
    </div>
  );

  return (
    <div className={`admin-menu-select ${compact ? 'admin-menu-select--compact' : ''}`}>
      <Text as="span" variant="bodyMd">{label}</Text>
      <Popover
        active={active}
        activator={activator}
        fluidContent
        onClose={() => setActive(false)}
        preferredPosition="below"
        preferredAlignment="left"
        captureOverscroll
      >
        <div
          id={id}
          className={`admin-menu-select__popover ${compact ? 'admin-menu-select__popover--compact' : ''}`}
          style={popoverStyle}
        >
          <div className="admin-menu-select__options" role="listbox" aria-label={label}>
            {options.map((option) => {
              const selected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`admin-menu-select__option ${selected ? 'is-selected' : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    setActive(false);
                  }}
                >
                  <div className="admin-menu-select__option-copy">
                    <span className="admin-menu-select__option-label">{option.label}</span>
                    {option.helpText ? (
                      <span className="admin-menu-select__option-help">{option.helpText}</span>
                    ) : null}
                  </div>
                  <span className="admin-menu-select__option-icon" aria-hidden="true">
                    {selected ? <Icon source={CheckIcon} /> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </Popover>
    </div>
  );
}
